// Regras puras da trilha lúdica: divisão em levas, janela de nós visíveis e
// estado de cada nó. Nenhuma dependência de React, DOM ou rede — é o que
// permite testar todas as fronteiras sem montar componente.
//
// Diferença central em relação ao protótipo (Trilha_ludica/app): lá o total de
// fases era a constante 10. Aqui o total é o número de itens da atividade, que
// o professor define — então tudo é função de `totalFases`.

// A partir de 5 fases a trilha se divide em 2 levas (a 2ª metade só é revelada
// quando o aluno chega nela). Abaixo disso, esconder metade de 3 ou 4 nós não
// cria expectativa, só confunde — então a trilha inteira aparece de uma vez.
//
// É este número, e só ele, que decide quantos NÓS ESPECIAIS a trilha tem: a
// regra de recompensas põe card raro na última fase de cada leva, então 1 leva =
// 1 nó especial (o de finalização) e 2 levas = 2. Trilha curta com um só nó
// especial não é caso tratado à parte em lugar nenhum; é consequência daqui.
//
// Revisto em 31/07/2026 e MANTIDO em 5. O caso em discussão era N=5, o menor com
// 2 levas: ali a leva 2 tem 2 nós e 40% das fases dão card raro (contra 20% em
// N=10). Subir para 6 daria a N=5 uma leva só e um raro só, e deixaria a menor
// leva 2 possível com 3 nós. Ficou em 5 por decisão de produto — a cortina de
// neblina é o efeito mais elaborado do tabuleiro e o corte em 6 a tiraria de uma
// faixa de tamanho que o professor usa. A tabela de comportamento por tamanho
// está no ../README.md.
export const MINIMO_PARA_DUAS_LEVAS = 5;

export function totalDeLevas(totalFases) {
  if (totalFases <= 0) return 0;
  return totalFases >= MINIMO_PARA_DUAS_LEVAS ? 2 : 1;
}

/**
 * Última fase de uma leva. Com 2 levas o corte é em ceil(totalFases / 2), o que
 * deixa a leva 1 com a metade maior quando o total é ímpar — de propósito: a
 * 1ª leva é a que o aluno vê primeiro e onde o hábito se forma.
 */
export function fimDaLeva(leva, totalFases) {
  if (totalFases <= 0) return 0;
  if (totalDeLevas(totalFases) === 1) return totalFases;
  return leva === 1 ? Math.ceil(totalFases / 2) : totalFases;
}

export function levaDaFase(fase, totalFases) {
  if (totalFases <= 0) return 0;
  return fase <= fimDaLeva(1, totalFases) ? 1 : 2;
}

/**
 * Até que nó o tabuleiro é desenhado. A janela é a leva atual — foi o que
 * substituiu a janela fixa de 5 nós do protótipo. Efeito prático: o aviso
 * "mais fases pela frente" aparece exatamente enquanto o aluno está na leva 1,
 * para qualquer tamanho de atividade, e nunca mente.
 */
export function fimDaJanelaAtual(faseAtual, totalFases) {
  if (totalFases <= 0) return 0;
  const fase = Math.min(Math.max(faseAtual, 1), totalFases);
  return fimDaLeva(levaDaFase(fase, totalFases), totalFases);
}

export function temFasesEscondidas(faseAtual, totalFases) {
  return fimDaJanelaAtual(faseAtual, totalFases) < totalFases;
}

/**
 * Reproduz *exatamente* a regra de bloqueio da lista clássica do AVA
 * (components/Widgets/Widgets.js): um item está liberado se for o primeiro, se
 * a atividade não é sequencial, ou se o item **imediatamente anterior** está
 * 100% concluído.
 *
 * Ela olha só o anterior, não todos os anteriores. Isso importa: numa atividade
 * com itens concluídos fora de ordem (histórico de atividade livre, correção
 * manual), "primeiro pendente" travaria na trilha algo que a lista libera — as
 * duas visualizações da mesma atividade discordariam em silêncio.
 */
export function ehClicavel(indice, fases, sequencial) {
  if (!fases || indice < 0 || indice >= fases.length) return false;
  if (!sequencial) return true;
  if (indice === 0) return true;
  return fases[indice - 1].concluida === true;
}

/**
 * Índice do nó "atual" — o primeiro liberado e ainda não concluído. É onde o
 * avatar fica e para onde o tabuleiro rola. Devolve -1 quando não há nó atual
 * (trilha concluída ou lista vazia).
 */
export function indiceFaseAtual(fases, sequencial) {
  if (!fases) return -1;
  for (let i = 0; i < fases.length; i += 1) {
    if (!fases[i].concluida && ehClicavel(i, fases, sequencial)) return i;
  }
  return -1;
}

/**
 * Estado **visual** de um nó:
 * - `completed`  concluído (item 100%)
 * - `current`    o próximo a fazer (recebe o avatar)
 * - `available`  liberado, mas não é o próximo (atividade livre, ou conclusão
 *                fora de ordem — ver ehClicavel)
 * - `locked`     bloqueado
 *
 * Atenção: estado visual não decide clique. Quem decide é `ehClicavel` — um nó
 * `completed` continua clicável (a lista clássica permite reabrir um item já
 * concluído para revisão, e as duas visualizações têm de concordar).
 */
export function estadoDoNo(indice, fases, sequencial, indiceAtual) {
  if (!fases || indice < 0 || indice >= fases.length) return "locked";
  if (fases[indice].concluida) return "completed";
  if (indice === indiceAtual) return "current";
  return ehClicavel(indice, fases, sequencial) ? "available" : "locked";
}

export function totalConcluidas(fases) {
  if (!fases) return 0;
  return fases.filter(fase => fase.concluida).length;
}
